import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { PayrexxInstanceStatus, Vendor } from '../models/entities/vendor';
import { ApiGetSuccessResponse } from '../models/common/api-get-success-response';
import { UpdateVendorRequest } from '../requests/vendors/update-vendor.request';
import { UpdateTwoFactorAuthRequest } from '../requests/vendors/update-two-factor-auth.request';
import { ListingVendor } from '../models/listing-vendor';
import {
  VendorTranslationEntry,
  VendorTranslations,
  VendorTranslationStats,
} from '../models/entities/vendor-translation';

@Injectable({
  providedIn: 'root',
})
export class VendorsService {
  // Mémorise la liste publique : évite un rechargement à chaque retour sur la page
  private listingVendorsCache?: Promise<ApiGetSuccessResponse<ListingVendor[]>>;

  constructor(private apiService: ApiService) {}

  findAllForListing(): Promise<ApiGetSuccessResponse<ListingVendor[]>> {
    if (!this.listingVendorsCache) {
      this.listingVendorsCache = firstValueFrom(
        this.apiService.get('vendors'),
      ).catch((exception) => {
        // En cas d'échec, on n'empoisonne pas le cache : on pourra réessayer
        this.listingVendorsCache = undefined;
        throw exception;
      });
    }

    return this.listingVendorsCache;
  }

  store(formData: object) {
    return this.apiService.post('vendors', formData);
  }

  findOne(
    slug: string,
    { ignoreServerRenderedResponse = false } = {},
  ): Observable<Vendor> {
    return this.apiService
      .get('vendors/' + slug, undefined, { ignoreServerRenderedResponse })
      .pipe(map((response: ApiGetSuccessResponse<Vendor>) => response.data));
  }

  update(request: UpdateVendorRequest) {
    if (request.logo || request.general_terms) {
      return this.apiService.post(
        `vendors`,
        ApiService.buildFormData(request, true),
      );
    } else {
      return this.apiService.put(`vendors`, request);
    }
  }

  /**
   * Conditions générales d'une seule langue : chaque langue a son propre
   * fichier, les autres restent inchangées.
   */
  uploadGeneralTerms(file: File, locale: string): Promise<Vendor> {
    return this.apiService.post(
      'vendors/general-terms',
      ApiService.buildFormData({ general_terms: file, locale }),
    );
  }

  deleteGeneralTerms(locale: string): Promise<Vendor> {
    return this.apiService.delete('vendors/general-terms', { locale });
  }

  createPayrexxMerchantInstance(): Promise<void> {
    return this.apiService.post(`vendors/setup-payrexx`, {});
  }

  findPayrexxVerificationStatus(): Promise<
    ApiGetSuccessResponse<{ status: PayrexxInstanceStatus }>
  > {
    return firstValueFrom(
      this.apiService.get(`vendors/payrexx-verification-status`, {}),
    );
  }

  updateTwoFactorAuth(request: UpdateTwoFactorAuthRequest) {
    return this.apiService.put(`vendors/two-factor-authentication`, request);
  }

  updateSupportAccess(request: { support_access_enabled: boolean }) {
    return this.apiService.put(`vendors/support-access`, request);
  }

  updateTestMode(request: { test_mode: boolean }) {
    return this.apiService.put(`vendors/test-mode`, request);
  }

  updateBookingPeriodConstraint(request: {
    booking_period_constraint: string | null;
  }) {
    return this.apiService.put(`vendors/booking-period-constraint`, request);
  }

  updateGracePeriod(request: { grace_period_hours: number | null }) {
    return this.apiService.put(`vendors/grace-period`, request);
  }

  storePaymentMethod(request: { name: string }) {
    return this.apiService.post(`vendors/payment-methods`, request);
  }

  deletePaymentMethod(request: { name: string }) {
    return this.apiService.delete(`vendors/payment-methods`, request);
  }

  /** Avancement de la traduction, langue par langue. */
  findTranslationStats(): Promise<VendorTranslationStats> {
    return firstValueFrom(
      this.apiService
        .get('vendors/translations/stats')
        .pipe(
          map(
            (response: ApiGetSuccessResponse<VendorTranslationStats>) =>
              response.data,
          ),
        ),
    );
  }

  /** Tout le contenu traduisible de l'organisation, pour une langue cible. */
  findTranslations(locale: string): Promise<VendorTranslations> {
    return firstValueFrom(
      this.apiService
        .get('vendors/translations', { locale })
        .pipe(
          map(
            (response: ApiGetSuccessResponse<VendorTranslations>) =>
              response.data,
          ),
        ),
    );
  }

  /**
   * N'envoie que les traductions modifiées : la langue principale n'est jamais
   * de la partie, elle se modifie depuis les écrans d'édition.
   */
  updateTranslations(
    locale: string,
    translations: VendorTranslationEntry[],
  ): Promise<VendorTranslations> {
    return this.apiService.put('vendors/translations', { locale, translations });
  }
}
